import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
  ArrowRight,
  CheckCircle,
  Database,
  ExternalLink,
  FileText,
  Info,
  MapPin,
  RefreshCw,
  Search,
  type LucideIcon,
} from 'lucide-react';
import SEO, { createBreadcrumbStructuredData, createFAQStructuredData } from '../components/SEO';
import { howItWorksContent } from '../content/howItWorks';
import { useAnalytics } from '../hooks/useAnalytics';
import { borderRadius, breakpoints, colors, spacing, typography } from '../styles/theme';

const SITE_URL = 'https://www.eventfinder.site';
const stepIcons: LucideIcon[] = [Search, FileText, CheckCircle, Database, MapPin, RefreshCw];

const Page = styled.article`
  width: 100%;
  background: ${colors.background.primary};
`;

const Container = styled.div`
  width: min(100% - ${spacing[8]}, 1080px);
  margin: 0 auto;

  @media (max-width: ${breakpoints.sm}) {
    width: min(100% - ${spacing[6]}, 1080px);
  }
`;

const Hero = styled.header`
  padding: ${spacing[16]} 0 ${spacing[12]};
  background: ${colors.background.dark};
  color: ${colors.white};

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[12]} 0 ${spacing[10]};
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 ${spacing[3]};
  color: ${colors.primary[200]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  letter-spacing: 0;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  max-width: 760px;
  margin: 0 0 ${spacing[5]};
  color: ${colors.white};
  font-size: ${typography.fontSizes['4xl']};
  line-height: ${typography.lineHeights.tight};
  letter-spacing: 0;

  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes['3xl']};
  }
`;

const HeroCopy = styled.p`
  max-width: 780px;
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: ${typography.fontSizes.lg};
  line-height: ${typography.lineHeights.relaxed};
`;

const Section = styled.section`
  padding: ${spacing[16]} 0;

  &[data-tone='soft'] {
    background: ${colors.background.secondary};
  }

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[12]} 0;
  }
`;

const SectionHeader = styled.div`
  max-width: 760px;
  margin-bottom: ${spacing[10]};
`;

const SectionTitle = styled.h2`
  margin: 0 0 ${spacing[3]};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes['3xl']};
  line-height: ${typography.lineHeights.tight};
  letter-spacing: 0;

  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const SectionIntro = styled.p`
  margin: 0;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.lg};
  line-height: ${typography.lineHeights.relaxed};
`;

const ProcessList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid ${colors.border.normal};
`;

const ProcessStep = styled.li`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: ${spacing[6]};
  padding: ${spacing[8]} 0;
  border-bottom: 1px solid ${colors.border.normal};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 56px minmax(0, 1fr);
    gap: ${spacing[4]};
    padding: ${spacing[6]} 0;
  }
`;

const StepMarker = styled.div`
  display: flex;
  width: 64px;
  height: 64px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.lg};
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};

  @media (max-width: ${breakpoints.sm}) {
    width: 48px;
    height: 48px;
  }
`;

const StepNumber = styled.span`
  display: block;
  margin-bottom: ${spacing[1]};
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.bold};
`;

const StepTitle = styled.h3`
  margin: 0 0 ${spacing[2]};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.xl};
  line-height: ${typography.lineHeights.tight};
  letter-spacing: 0;
`;

const StepDescription = styled.p`
  margin: 0 0 ${spacing[4]};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

const DetailList = styled.ul`
  display: grid;
  gap: ${spacing[2]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Detail = styled.li`
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: ${spacing[2]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  line-height: ${typography.lineHeights.relaxed};

  svg {
    margin-top: 4px;
    color: ${colors.success[600]};
  }
`;

const QualityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${spacing[8]};

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${spacing[6]};
  }
`;

const QualityItem = styled.div`
  padding-top: ${spacing[5]};
  border-top: 3px solid ${colors.secondary[400]};
`;

const QualityTitle = styled.h3`
  margin: 0 0 ${spacing[2]};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.lg};
  letter-spacing: 0;
`;

const QualityDescription = styled.p`
  margin: 0;
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

const Reminder = styled.p`
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: ${spacing[3]};
  max-width: 780px;
  margin: ${spacing[8]} 0 0;
  padding: ${spacing[5]} 0;
  border-top: 1px solid ${colors.border.normal};
  border-bottom: 1px solid ${colors.border.normal};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};

  svg {
    margin-top: 3px;
    color: ${colors.secondary[600]};
  }
`;

const FaqList = styled.div`
  border-top: 1px solid ${colors.border.normal};
`;

const Faq = styled.details`
  border-bottom: 1px solid ${colors.border.normal};

  summary {
    padding: ${spacing[5]} 0;
    color: ${colors.text.primary};
    font-size: ${typography.fontSizes.lg};
    font-weight: ${typography.fontWeights.semibold};
    cursor: pointer;
  }

  p {
    max-width: 820px;
    margin: 0 0 ${spacing[6]};
    color: ${colors.text.secondary};
    line-height: ${typography.lineHeights.relaxed};
  }
`;

const ExploreBand = styled.section`
  padding: ${spacing[12]} 0;
  background: ${colors.primary[800]};
  color: ${colors.white};
`;

const ExploreLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[8]};

  @media (max-width: ${breakpoints.md}) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ExploreCopy = styled.div`
  h2 {
    margin: 0 0 ${spacing[2]};
    color: ${colors.white};
    font-size: ${typography.fontSizes['2xl']};
    letter-spacing: 0;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    line-height: ${typography.lineHeights.relaxed};
  }
`;

const ExploreLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
`;

const PrimaryLink = styled.a`
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[5]};
  border-radius: ${borderRadius.lg};
  background: ${colors.white};
  color: ${colors.primary[800]};
  font-weight: ${typography.fontWeights.semibold};
  text-decoration: none;

  &:hover {
    background: ${colors.primary[50]};
  }
`;

const SecondaryLink = styled(PrimaryLink)`
  border: 1px solid rgba(255, 255, 255, 0.48);
  background: transparent;
  color: ${colors.white};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const HowItWorksPage: React.FC = () => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent({
      action: 'page_view',
      category: 'navigation',
      label: 'how_it_works',
      custom_parameters: {
        page_title: howItWorksContent.title,
        page_path: '/how-it-works',
      },
    });
  }, [trackEvent]);

  return (
    <Page data-editorial-page="how-it-works" data-prerender-ready="true">
      <SEO
        title={howItWorksContent.title}
        description={howItWorksContent.description}
        keywords="how Event Finder works, event discovery, event listings, event search, duplicate events, event maps"
        canonical={`${SITE_URL}/how-it-works`}
        type="article"
        modifiedTime={howItWorksContent.lastModified}
        languageAlternates={[
          { hrefLang: 'en', href: `${SITE_URL}/how-it-works` },
          { hrefLang: 'x-default', href: `${SITE_URL}/how-it-works` },
        ]}
        structuredData={[
          createFAQStructuredData([...howItWorksContent.faqs]),
          createBreadcrumbStructuredData([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'How Event Finder Works', url: `${SITE_URL}/how-it-works` },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: howItWorksContent.title,
            description: howItWorksContent.description,
            url: `${SITE_URL}/how-it-works`,
            dateModified: howItWorksContent.lastModified,
            inLanguage: 'en',
          },
        ]}
      />

      <Hero>
        <Container>
          <Eyebrow>From public listings to searchable events</Eyebrow>
          <HeroTitle>{howItWorksContent.title}</HeroTitle>
          <HeroCopy>{howItWorksContent.introduction}</HeroCopy>
        </Container>
      </Hero>

      <Section aria-labelledby="process-title">
        <Container>
          <SectionHeader>
            <SectionTitle id="process-title">The event discovery process</SectionTitle>
            <SectionIntro>Six practical steps turn many different website layouts into event records you can search, compare, and open on a map.</SectionIntro>
          </SectionHeader>
          <ProcessList>
            {howItWorksContent.steps.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <ProcessStep key={step.title}>
                  <div>
                    <StepNumber>STEP {index + 1}</StepNumber>
                    <StepMarker aria-hidden="true"><Icon size={28} /></StepMarker>
                  </div>
                  <div>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                    <DetailList>
                      {step.details.map((detail) => (
                        <Detail key={detail}>
                          <CheckCircle size={16} aria-hidden="true" />
                          <span>{detail}</span>
                        </Detail>
                      ))}
                    </DetailList>
                  </div>
                </ProcessStep>
              );
            })}
          </ProcessList>
        </Container>
      </Section>

      <Section data-tone="soft" aria-labelledby="quality-title">
        <Container>
          <SectionHeader>
            <SectionTitle id="quality-title">What to know about event data</SectionTitle>
            <SectionIntro>Automated discovery makes a large catalog possible, but the source website remains the best place to confirm a plan.</SectionIntro>
          </SectionHeader>
          <QualityGrid>
            {howItWorksContent.qualityNotes.map((note) => (
              <QualityItem key={note.title}>
                <QualityTitle>{note.title}</QualityTitle>
                <QualityDescription>{note.description}</QualityDescription>
              </QualityItem>
            ))}
          </QualityGrid>
          <Reminder>
            <Info size={20} aria-hidden="true" />
            <span>Event Finder uses automated checks, but websites and event plans can change. Follow the source link on an event page before registering or traveling.</span>
          </Reminder>
        </Container>
      </Section>

      <Section aria-labelledby="faq-title">
        <Container>
          <SectionHeader><SectionTitle id="faq-title">Common questions</SectionTitle></SectionHeader>
          <FaqList>
            {howItWorksContent.faqs.map((faq) => (
              <Faq key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </Faq>
            ))}
          </FaqList>
        </Container>
      </Section>

      <ExploreBand aria-labelledby="explore-title">
        <Container>
          <ExploreLayout>
            <ExploreCopy>
              <h2 id="explore-title">See the catalog for yourself</h2>
              <p>Browse upcoming events, or read what has changed in the latest Event Finder updates.</p>
            </ExploreCopy>
            <ExploreLinks>
              <PrimaryLink href="/browse">Browse events <ArrowRight size={18} aria-hidden="true" /></PrimaryLink>
              <SecondaryLink href="/latest-updates">Latest updates <ExternalLink size={17} aria-hidden="true" /></SecondaryLink>
            </ExploreLinks>
          </ExploreLayout>
        </Container>
      </ExploreBand>
    </Page>
  );
};

export default HowItWorksPage;